/** @jsx h */
import { Component, h, JSX } from 'preact';
import { RootStore } from '../../../../common/js/stores/root.store';
import { VideoStore } from '../../../../common/js/stores/video.store';
import { MediaFile } from '../../lib/vast/media-file';

interface VideoTagProps {
  preload?: string;
  poster?: string | null;
  wrapperStyle?: h.JSX.CSSProperties;
  videoStyle?: h.JSX.CSSProperties;
  vpaidContainerStyle?: h.JSX.CSSProperties;
  muted?: boolean;
  mediaFiles?: MediaFile[];
  onVideoBufferingStop?: () => void;
  onVideoBufferingStart?: () => void;
  onVideoComplete?: () => void;
  onVideoProgress?: () => void;
  onVideoLoadStart?: () => void;
  onVideoError?: () => void;
  onVideoReady?: () => void;
}

type StyleMapKey = 'wrapperStyle' | 'videoStyle' | 'vpaidContainerStyle';

type EventName =
  | 'onVideoLoadStart'
  | 'onVideoProgress'
  | 'onVideoComplete'
  | 'onVideoError'
  | 'onVideoBufferingStart'
  | 'onVideoBufferingStop'
  | 'onVideoReady';

/**
 * WARNING
 *
 * This Component is here to remove the Video tag and its container from Preact Rerendering control.
 *
 * A lot of VPAID ad units are using DOM elements to dispatch VPAID events (the container div or the video tag).
 * However, when preact makes its render loop, its reconciliation algorithm considers the container div stale
 * and "updates" it. Doing so, it cuts off all event listeners on the div, removing all VPAID event listeners,
 * preventing us to display the Ad.
 *
 * By instructing preact not to render the component (through shouldComponentUpdate to false),
 * and by manually updating the props that should be (updateComponent), the Video Player is able
 * to run the VPAID ad unit properly.
 *
 */
export class VideoTag extends Component<VideoTagProps> {
  videoPlayer!: HTMLVideoElement;
  videoPlayerContainer!: HTMLDivElement;
  vpaidContainer!: HTMLDivElement;
  areMediaFilesPresent: boolean = false;
  videoStore: VideoStore;

  currentProps: VideoTagProps | null = null;

  constructor(props: VideoTagProps) {
    super(props);
    this.onEvent = this.onEvent.bind(this);
    this.videoStore = RootStore.getInstance().videoStore;
  }

  shouldComponentUpdate(props: VideoTagProps): boolean {
    this.updateComponent(props);
    return false;
  }

  onEvent(eventName: EventName): void {
    const handler = this.props[eventName];
    if (eventName && handler) {
      handler();
    }
  }

  componentDidMount(): void {
    this.videoPlayer = document.getElementById('video') as HTMLVideoElement;
    this.videoPlayerContainer = document.getElementById(
      'no-re-rendered-video'
    ) as HTMLDivElement;
    this.vpaidContainer = document.getElementById(
      'vpaid-main-container'
    ) as HTMLDivElement;

    this.videoStore.setVideoPlayer(this.videoPlayer);
    this.updateComponent(this.props);
  }

  render(): JSX.Element {
    return (
      <div id="no-re-rendered-video" style="height:100%; width:100%;">
        <div
          id="videoContainer"
          style="height: 100%; width:100%; position: absolute;"
        >
          <video
            id="video"
            playsinline={true}
            onLoadStart={() => this.onEvent('onVideoLoadStart')}
            onLoadedMetadata={() => this.videoStore.refreshVideoSizes()}
            onTimeUpdate={() => this.onEvent('onVideoProgress')}
            onEnded={() => this.onEvent('onVideoComplete')}
            onError={() => this.onEvent('onVideoError')}
            onCanPlay={() => this.onEvent('onVideoReady')}
            onWaiting={() => this.onEvent('onVideoBufferingStart')}
            onPlaying={() => this.onEvent('onVideoBufferingStop')}
          />
        </div>
        <div id="vpaid-main-container"></div>
      </div>
    );
  }

  updateComponent(props: VideoTagProps): void {
    this.updateMediaFiles(props.mediaFiles || []);
    this.updatePosterAttribute(props);
    this.updateStyleAttribute(props, this.videoPlayerContainer, 'wrapperStyle');
    this.updateStyleAttribute(props, this.videoPlayer, 'videoStyle');
    this.updateStyleAttribute(
      props,
      this.vpaidContainer,
      'vpaidContainerStyle'
    );
    this.updatePreloadAttribute(props);
    this.updateMutedAttribute(props);

    this.currentProps = props;
  }

  updateMutedAttribute(props: VideoTagProps): void {
    if (!this.currentProps || props.muted !== this.currentProps.muted) {
      this.videoPlayer.muted = props.muted || false;
    }
  }

  updatePosterAttribute(props: VideoTagProps): void {
    if (
      (!this.currentProps || props.poster !== this.currentProps.poster) &&
      props.poster !== null &&
      props.poster !== undefined
    ) {
      this.videoPlayer.poster = props.poster;
    }
  }

  updatePreloadAttribute(props: VideoTagProps): void {
    if (!this.currentProps || props.preload !== this.currentProps.preload) {
      this.videoPlayer.preload = (props.preload ||
        '') as typeof HTMLVideoElement.prototype.preload;
    }
  }

  updateStyleAttribute(
    props: VideoTagProps,
    domElement: HTMLElement,
    styleMap: StyleMapKey
  ): void {
    if (!this.currentProps || props[styleMap] !== this.currentProps[styleMap]) {
      let style = '';
      const styleObj = props[styleMap];
      if (styleObj) {
        for (const s in styleObj) {
          style += `${s}:${styleObj[s]};`;
        }
      }
      domElement.setAttribute('style', style);
    }
  }

  updateMediaFiles(nextMediaFiles: MediaFile[]): void {
    if (this.areMediaFilesPresent) {
      return;
    }
    this.appendMediaSources(nextMediaFiles);
    this.areMediaFilesPresent = true;
  }

  appendMediaSources(mediaFiles: MediaFile[]): void {
    mediaFiles.forEach(mediaFile => {
      const source = document.createElement('source');
      source.setAttribute('src', mediaFile.url);
      source.setAttribute('type', mediaFile.type);
      this.videoPlayer.appendChild(source);
    });
  }
}

export default VideoTag;
